import { Flex, Skeleton, Typography } from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';
import type { ReactNode } from 'react';

import { useGetStore } from '@jane/shared/data-access';
import type { CartLineItems } from '@jane/shared/types';

import { selectCart } from '../../../../customer/redux/cart';
import { useCustomerSelector } from '../../../../customer/selectors';
import { reservationModeLabel } from '../../../../lib/store';
import { SummaryWrapper } from './cartDrawerSummary.styles';

interface CartDrawerSummaryProps {
  cartLineItems: CartLineItems;
  isChangingReservationModes: boolean;
}

interface LineItem {
  amount: number | string;
  id: string;
  label: string;
  text?: ReactNode;
}

const MaxSpecialsDisclaimer = ({
  maxSpecialsPerCart,
}: {
  maxSpecialsPerCart: number | null;
}) => {
  if (maxSpecialsPerCart) {
    const pluralizedLabel = maxSpecialsPerCart > 1 ? 'specials' : 'special';
    return (
      <Typography color="grays-mid" as="p" my={8} variant="mini">
        This store supports {maxSpecialsPerCart + ' ' + pluralizedLabel + ' '}
        per order. Don't worry, we've already selected the {
          pluralizedLabel
        }{' '}
        that saves you the most.
      </Typography>
    );
  }
  return null;
};

export const CartDrawerSummary = ({
  cartLineItems,
  isChangingReservationModes,
}: CartDrawerSummaryProps) => {
  const { cart, deliveryAddress } = useCustomerSelector(selectCart);
  const {
    preDiscountSubtotal,
    deliveryFee,
    discountTotal,
    roundingAmount,
    serviceFee,
    storeTax,
    salesTax,
    tipAmount,
  } = cartLineItems;

  const {
    reservation_mode: reservationMode,
    max_specials_per_cart: maxSpecialsPerCart,
    max_specials_per_cart_hit: maxSpecialsPerCartHit,
  } = cart;

  const { data: store } = useGetStore(cart?.store?.id);
  const { delivery, tax_included } = store;

  const applyDeliveryFee = delivery && reservationMode === 'delivery';

  const hasJanePayServiceFee =
    store?.jane_pay_integration?.consumer_fee_per_transaction ||
    store?.jane_pay_integration?.consumer_fee_rate ||
    cart.jane_pay_service_fee_amount > 0;

  const lineItems: LineItem[] = [
    discountTotal > 0 && {
      id: 'savings',
      label: 'Estimated savings',
      amount: -discountTotal,
    },
    {
      id: 'service-fee',
      label: 'Estimated service fee',
      amount: serviceFee
        ? serviceFee
        : store?.jane_pay_integration?.enabled && hasJanePayServiceFee
          ? 'Calculated at checkout'
          : 0,
    },
    applyDeliveryFee && {
      id: 'delivery-fee',
      label: `Estimated ${reservationModeLabel(store, 'delivery')} fee`,
      amount: deliveryAddress ? deliveryFee || 0 : 'Enter address',
    },
    storeTax > 0 && {
      id: 'localTaxes',
      label: 'Estimated local tax',
      amount: storeTax,
    },
    !tax_included && {
      id: 'taxes',
      label: 'Estimated sales tax',
      amount: salesTax,
    },
    tipAmount > 0 && {
      id: 'tipAmount',
      label: 'Delivery tip',
      amount: tipAmount,
    },
    roundingAmount != 0 && {
      id: 'rounding',
      label: 'Rounding',
      amount: roundingAmount,
    },
  ].filter(Boolean) as LineItem[];

  const recalculatedFields = ['delivery-fee', 'service-fee'];

  return (
    <SummaryWrapper flexDirection="column">
      <Flex justifyContent="space-between">
        <Typography color="grays-dark">Subtotal</Typography>
        <Typography color="grays-dark">
          {formatCurrency(preDiscountSubtotal)}
        </Typography>
      </Flex>
      {lineItems.map((item) => (
        <Flex mt={8} justifyContent="space-between" key={item.id}>
          <Typography color="grays-dark">{item.label}</Typography>
          {isChangingReservationModes &&
          recalculatedFields.includes(item.id) ? (
            <Skeleton.Bone ml="auto" width={50} />
          ) : (
            <Typography color="grays-dark">
              {typeof item.amount === 'string'
                ? item.amount
                : item.amount
                  ? formatCurrency(item.amount)
                  : 'Free'}
            </Typography>
          )}
        </Flex>
      ))}

      {maxSpecialsPerCartHit && (
        <MaxSpecialsDisclaimer maxSpecialsPerCart={maxSpecialsPerCart} />
      )}
    </SummaryWrapper>
  );
};
